import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const AlertNotifications = ({ onDismissAlert, onResolveAlert }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const now = new Date();
        const last24h = new Date(now - 24 * 60 * 60 * 1000).toISOString();
        const last7d  = new Date(now - 7  * 24 * 60 * 60 * 1000).toISOString();
        const next7d  = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();

        const [failedPayments, cancelledSubs, expiringSubs, newUsers] = await Promise.all([
          // 1. revenue_records → status='failed', created_at, amount
          supabase
            .from('revenue_records')
            .select('id, status, amount, currency, created_at')
            .eq('status', 'failed')
            .gte('created_at', last7d),

          // 2. user_subscriptions → status='cancelled', cancelled_at
          supabase
            .from('user_subscriptions')
            .select('id, status, cancelled_at')
            .eq('status', 'cancelled')
            .gte('cancelled_at', last7d),

          // 3. user_subscriptions → end_date ≤ 7 วัน, status='active'
          supabase
            .from('user_subscriptions')
            .select('id, status, end_date')
            .eq('status', 'active')
            .not('end_date', 'is', null)
            .lte('end_date', next7d)
            .gte('end_date', now.toISOString()),

          // 4. user_profiles → created_at ใน 24h ล่าสุด
          supabase
            .from('user_profiles')
            .select('id, created_at')
            .gte('created_at', last24h),
        ]);

        const derived = [];

        // ── Payment Failures (critical) ─────────────────────────────────────
        const failedCount = failedPayments.data?.length || 0;
        if (failedCount > 0) {
          const latestFail = failedPayments.data.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )[0];
          derived.push({
            id: 'payment-failure',
            type: 'critical',
            title: 'Payment Failures Detected',
            message: `${failedCount} payment${failedCount > 1 ? 's' : ''} failed in the last 7 days. Review revenue records.`,
            timestamp: new Date(latestFail.created_at),
            resolved: false,
          });
        }

        // ── Cancelled Subscriptions (warning) ───────────────────────────────
        const cancelledCount = cancelledSubs.data?.length || 0;
        if (cancelledCount > 0) {
          const latestCancel = cancelledSubs.data.sort(
            (a, b) => new Date(b.cancelled_at) - new Date(a.cancelled_at)
          )[0];
          derived.push({
            id: 'cancelled-subs',
            type: 'warning',
            title: 'Subscription Cancellations',
            message: `${cancelledCount} subscription${cancelledCount > 1 ? 's' : ''} cancelled in the last 7 days.`,
            timestamp: new Date(latestCancel.cancelled_at),
            resolved: false,
          });
        }

        // ── Expiring Subscriptions (warning) ────────────────────────────────
        const expiringCount = expiringSubs.data?.length || 0;
        if (expiringCount > 0) {
          derived.push({
            id: 'expiring-subs',
            type: 'warning',
            title: 'Subscriptions Expiring Soon',
            message: `${expiringCount} active subscription${expiringCount > 1 ? 's' : ''} will expire within 7 days.`,
            timestamp: now,
            resolved: false,
          });
        }

        // ── New User Signups (info) ──────────────────────────────────────────
        const newCount = newUsers.data?.length || 0;
        if (newCount > 0) {
          const latestSignup = newUsers.data.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )[0];
          derived.push({
            id: 'new-users',
            type: 'info',
            title: 'New User Registrations',
            message: `${newCount} new user${newCount > 1 ? 's' : ''} signed up in the last 24 hours.`,
            timestamp: new Date(latestSignup.created_at),
            resolved: false,
          });
        }

        setAlerts(derived);
      } catch (err) {
        console.error('AlertNotifications fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, []);

  const getAlertStyles = (type) => {
    const styles = {
      critical: {
        bg: 'bg-error/10',
        border: 'border-error',
        icon: 'AlertCircle',
        iconColor: 'text-error',
        badge: 'bg-error text-error-foreground'
      },
      warning: {
        bg: 'bg-warning/10',
        border: 'border-warning',
        icon: 'AlertTriangle',
        iconColor: 'text-warning',
        badge: 'bg-warning text-warning-foreground'
      },
      info: {
        bg: 'bg-primary/10',
        border: 'border-primary',
        icon: 'Info',
        iconColor: 'text-primary',
        badge: 'bg-primary text-primary-foreground'
      }
    };
    return styles?.[type] || styles?.info;
  };

  const handleResolve = (alertId) => {
    setAlerts(prev => prev?.map(alert => 
      alert?.id === alertId ? { ...alert, resolved: true } : alert
    ));
    onResolveAlert(alertId);
  };

  const handleDismiss = (alertId) => {
    setAlerts(prev => prev?.filter(alert => alert?.id !== alertId));
    onDismissAlert(alertId);
  };

  const formatTimestamp = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const activeAlerts = alerts?.filter(alert => !alert?.resolved);
  const criticalCount = activeAlerts?.filter(a => a?.type === 'critical')?.length;
  const warningCount = activeAlerts?.filter(a => a?.type === 'warning')?.length;

  if (loading) return (
    <div className="bg-card rounded-lg border border-border shadow-soft p-12 text-center">
      <p className="text-muted-foreground text-sm">Loading alerts...</p>
    </div>
  );

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="Bell" size={24} className="text-primary" />
              Alert Notifications
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {activeAlerts?.length} active alerts
              {criticalCount > 0 && ` • ${criticalCount} critical`}
              {warningCount > 0 && ` • ${warningCount} warnings`}
            </p>
          </div>
          {activeAlerts?.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              iconName="CheckCheck"
              iconPosition="left"
              onClick={() => alerts?.forEach(alert => handleResolve(alert?.id))}
            >
              Resolve All
            </Button>
          )}
        </div>
      </div>
      <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
        {activeAlerts?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="CheckCircle" size={48} className="mx-auto mb-4 text-success" />
            <p className="text-foreground font-medium mb-1">All Clear!</p>
            <p className="text-sm text-muted-foreground">No active alerts at this time</p>
          </div>
        ) : (
          activeAlerts?.map((alert) => {
            const styles = getAlertStyles(alert?.type);
            return (
              <div key={alert?.id} className={`p-6 ${styles?.bg} border-l-4 ${styles?.border}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full bg-card flex items-center justify-center flex-shrink-0 ${styles?.iconColor}`}>
                    <Icon name={styles?.icon} size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-foreground">{alert?.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles?.badge}`}>
                          {alert?.type?.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(alert?.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-foreground mb-4">{alert?.message}</p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Check"
                        iconPosition="left"
                        onClick={() => handleResolve(alert?.id)}
                      >
                        Resolve
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="X"
                        onClick={() => handleDismiss(alert?.id)}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AlertNotifications;